import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, forkJoin, of, timer } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { AgentService, BatchItem, BatchItemState, BatchStatus } from './agent.service';
import { ToasterService } from '../utils/toaster.service';
import { batchToast, itemToast } from '../utils/ai-batch-toast.util';

/**
 * Watches every bulk upload this user has running, from one place.
 *
 * <p><b>Why one place.</b> Both `<app-md-ai-panel>` — mounted globally in the auth layout — and
 * the AI-First page are alive at once on `/meal-dynamics-ai`. Two components each polling and
 * each raising toasts means two toasts per invoice, and the user cannot tell which surface is
 * lying. A root service polls once and both render from it.
 *
 * <p><b>Why it survives the panel closing.</b> The user's fourth ask: the batch runs in the
 * background and they should not be pinned to the page. A component-owned poll dies with the
 * component, so closing the panel or navigating away lost the progress. This is `providedIn:
 * 'root'`, so it outlives both surfaces and keeps reporting.
 *
 * <p><b>Toasts are for state changes only.</b> The status endpoint returns the whole batch every
 * tick, so the same finished invoice appears in every response. Toasting on presence would mean
 * one toast per invoice per 2.5 seconds. The last-seen state per item is what makes it once.
 *
 * <p>The agent also writes the same outcomes into the conversation, which is what a user sees
 * when they come back tomorrow. These toasts are for the user who is here now and looking
 * somewhere else.
 */

/** How often to ask, awake and asleep. A hidden tab is not watching anything. */
const VISIBLE_INTERVAL_MS = 2_500;
const HIDDEN_INTERVAL_MS = 5_000;

/**
 * How many unanswered polls before a batch is given up on.
 *
 * <p>`AgentService.getBatchStatus` maps every failure to `null`, so a dropped connection and a
 * batch that genuinely no longer exists look identical here. Giving up on the first null would
 * abandon a live batch over one blip; never giving up would spin forever on a dead one. Three
 * consecutive misses is about seven seconds — long enough to ride out a blip, short enough that
 * a batch lost with its replica is reported quickly.
 */
const MISSES_BEFORE_GIVING_UP = 3;

export interface BatchTrackerEvent {
  kind: 'item' | 'batch' | 'lost';
  batchId: string;
  item?: BatchItem;
  status?: BatchStatus;
}

/** Terminal states — the ones worth telling someone about. */
const TERMINAL: readonly BatchItemState[] = ['created', 'skipped', 'needs-attention', 'failed'];

@Injectable({ providedIn: 'root' })
export class AiBatchTrackerService implements OnDestroy {
  private readonly batchesSubject = new BehaviorSubject<BatchStatus[]>([]);
  /** Every running batch, newest last. Both surfaces render their chips from this. */
  readonly batches$ = this.batchesSubject.asObservable();

  private readonly eventsSubject = new Subject<BatchTrackerEvent>();
  /** Fires as items and batches finish, so a surface can refresh its transcript. */
  readonly events$ = this.eventsSubject.asObservable();

  /** Batch ids currently being polled, with the session each belongs to. */
  private readonly watching = new Map<string, string>();
  /** Last state seen per item, so a toast fires on the change rather than on the presence. */
  private readonly seen = new Map<string, BatchItemState>();
  /** Consecutive unanswered polls per batch. See MISSES_BEFORE_GIVING_UP. */
  private readonly misses = new Map<string, number>();

  private poll: Subscription | null = null;
  private visible = true;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly agent: AgentService,
    private readonly toaster: ToasterService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stop();
  }

  /** Start watching a batch. Idempotent — starting the same one twice polls it once. */
  track(sessionId: string, batchId: string): void {
    if (this.watching.has(batchId)) return;

    this.watching.set(batchId, sessionId);
    this.start();
  }

  /**
   * Re-attach to whatever this session left running.
   *
   * <p>Called when a surface loads a session. The already-finished items are recorded as seen
   * without toasting: someone reopening the panel an hour later does not want five toasts about
   * invoices that finished while they were away — the transcript already tells them.
   */
  restore(sessionId: string): void {
    this.agent
      .getSessionBatches(sessionId)
      .pipe(takeUntil(this.destroy$), catchError(() => of({ batches: [], count: 0 })))
      .subscribe(result => {
        const running = (result.batches ?? []).filter(b => b.status === 'running');

        for (const batch of running) {
          for (const item of batch.items) {
            if (TERMINAL.includes(item.state)) this.seen.set(item.id, item.state);
          }
          this.watching.set(batch.batchId, sessionId);
        }

        if (running.length) {
          this.batchesSubject.next(running);
          this.start();
        }
      });
  }

  /** Whether a surface showing this is on screen. Slows the poll rather than stopping it. */
  setVisible(visible: boolean): void {
    if (this.visible === visible) return;

    this.visible = visible;
    if (this.poll) this.start();
  }

  cancel(batchId: string): void {
    this.agent
      .cancelBatch(batchId)
      .pipe(takeUntil(this.destroy$), catchError(() => of(null)))
      .subscribe(() => this.tick());
  }

  /** Forget everything. Used when a surface starts a new chat. */
  reset(): void {
    this.watching.clear();
    this.seen.clear();
    this.misses.clear();
    this.batchesSubject.next([]);
    this.stop();
  }

  private start(): void {
    this.poll?.unsubscribe();

    this.poll = timer(0, this.visible ? VISIBLE_INTERVAL_MS : HIDDEN_INTERVAL_MS)
      .pipe(
        switchMap(() => this.fetchAll()),
        takeUntil(this.destroy$),
      )
      .subscribe(statuses => this.apply(statuses));
  }

  private stop(): void {
    this.poll?.unsubscribe();
    this.poll = null;
  }

  private tick(): void {
    this.fetchAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe(statuses => this.apply(statuses));
  }

  private fetchAll() {
    const ids = [...this.watching.keys()];
    if (ids.length === 0) return of([] as Array<BatchStatus | null>);

    // One request per running batch, resolved together, so a slow one does not delay the others
    // into the next tick.
    return forkJoin(
      ids.map(id => this.agent.getBatchStatus(id).pipe(catchError(() => of(null)))),
    );
  }

  private apply(statuses: Array<BatchStatus | null>): void {
    const ids = [...this.watching.keys()];
    const running: BatchStatus[] = [];

    statuses.forEach((status, index) => {
      const batchId = ids[index];
      if (!batchId) return;

      if (!status) {
        const missed = (this.misses.get(batchId) ?? 0) + 1;
        this.misses.set(batchId, missed);

        if (missed < MISSES_BEFORE_GIVING_UP) {
          // Probably a blip. The batch is still running on the server, and dropping it here
          // would leave the user with no chips and no explanation for work that is still going.
          return;
        }

        // Gone — most likely with the replica that owned it. Saying so beats a spinner that
        // never resolves.
        this.watching.delete(batchId);
        this.misses.delete(batchId);
        this.eventsSubject.next({ kind: 'lost', batchId });
        return;
      }

      this.misses.delete(batchId);

      this.announceItems(batchId, status);

      if (this.isFinished(status)) {
        this.watching.delete(batchId);
        this.toast(batchToast(status));
        this.eventsSubject.next({ kind: 'batch', batchId, status });
      } else {
        running.push(status);
      }
    });

    this.batchesSubject.next(running);
    if (this.watching.size === 0) this.stop();
  }

  private announceItems(batchId: string, status: BatchStatus): void {
    for (const item of status.items) {
      if (!TERMINAL.includes(item.state)) continue;
      if (this.seen.get(item.id) === item.state) continue;

      this.seen.set(item.id, item.state);

      const toast = itemToast(item);
      if (toast) this.toast(toast);

      this.eventsSubject.next({ kind: 'item', batchId, item });
    }
  }

  private isFinished(status: BatchStatus): boolean {
    return status.status !== 'running'
      || (status.summary.queued === 0 && status.summary.creating === 0);
  }

  private toast(toast: { kind: string; message: string; title: string }): void {
    if (toast.kind === 'success') this.toaster.showSuccess(toast.message, toast.title);
    else if (toast.kind === 'warning') this.toaster.showWarning(toast.message, toast.title);
    else if (toast.kind === 'error') this.toaster.showError(toast.message, toast.title);
    else this.toaster.showInfo(toast.message, toast.title);
  }
}
